import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Banner } from '@/components/feedback/Banner'

/* The action FR-39 offers when a delete is refused (Story 7.1 AC 4).
 *
 * "The only offered action is merging" was true of the server and of nothing
 * else: `POST /directories/{clients,participants}/:id/merge` (the participants half
 * is `POST /counterparties/:id/merge` since S-22b) has been mounted
 * since Epic 7, `useMergeClients` had no consumer and `useMergeParticipants` did
 * not exist. So a refused delete named the referencing quotes and cases and then
 * pointed at an action the reader could not reach — which is a worse dead end
 * than no refusal at all, because it describes a way out that is not there.
 *
 * ONE COMPONENT FOR BOTH RECORD TYPES. Clients and counterparties merge
 * identically from the reader's side — choose the record to keep, confirm that
 * this one goes — and the differences are all server-side (a counterparty merge
 * is additionally refused across categories, and for one on a debit note).
 * `CounterpartyMergeSheet` feeds this sheet its same-category candidates. Two
 * sheets would be two places to fix the next thing either of them gets wrong.
 *
 * The SOURCE is the record being removed and is fixed by the caller; only the
 * TARGET is chosen here. Stated that way round because it is the direction users
 * get wrong: the sheet is opened from the row that is going away.
 */

export interface MergeCandidate {
  id: string
  name: string
}

export function MergeSheet({
  open,
  onClose,
  source,
  candidates,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  /** The record being folded away. Undefined closes the sheet. */
  source: MergeCandidate | undefined
  /** Everything it could be folded into. The source is filtered out here. */
  candidates: MergeCandidate[]
  pending?: boolean
  /** The server's refusal, already worded — merge can be refused too. */
  error?: string
  onConfirm: (targetId: string) => void
}) {
  const { t } = useTranslation()
  const [targetId, setTargetId] = useState('')

  /* Keyed on the source, so opening the sheet for a different record starts from
     an empty choice rather than inheriting the last one. A stale target here
     would merge the wrong pair, and this is an irreversible act. */
  const [lastSource, setLastSource] = useState(source?.id)
  if (source?.id !== lastSource) {
    setLastSource(source?.id)
    setTargetId('')
  }

  // Never itself: the server refuses it (ErrMergeIntoSelf) and offering it is
  // an invitation to meet that refusal.
  const options = candidates.filter((candidate) => candidate.id !== source?.id)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('directories.merge.title')}
      pending={pending}
      error={error}
      submitLabel={t('directories.merge.confirm')}
      /* Disabled only while nothing is chosen, which is a state the reader can
         see the cause of — the select above it is empty. Every OTHER refusal on
         this sheet is worded rather than expressed as a dead control
         (UX-DR-22). */
      disabled={targetId === ''}
      onSubmit={() => {
        if (targetId === '') return
        onConfirm(targetId)
      }}
    >
      {/* Above the field, because it describes what pressing the button does to
          a record that is NOT the one named in the select — and it cannot be
          undone. A Banner rather than a FormSheet prop, which does not exist:
          this is a standing statement about the act, not a validation message. */}
      <Banner tone="attention" title={t('directories.merge.action')}>
        {t('directories.merge.description')}
      </Banner>

      <Field label={t('directories.merge.target')} required>
        {(props) => (
          <Select
            value={targetId}
            onChange={setTargetId}
            options={[
              { value: '', label: t('directories.merge.targetPlaceholder') },
              ...options.map((candidate) => ({
                value: candidate.id,
                label: candidate.name,
              })),
            ]}
            {...props}
          />
        )}
      </Field>
    </FormSheet>
  )
}
